// Client-side image compression for cleaning staff photo uploads.
// Target: max 1280px long edge, quality 0.65, output WebP if supported.
// Returns a data URL (base64) targeting < 250KB.

const MAX_DIM = 1280;
const TARGET_QUALITY = 0.65;
const MAX_SIZE_BYTES = 250 * 1024; // 250KB

export async function compressImage(file) {
  const dataUrl = await readFileAsDataURL(file);
  const img = await loadImage(dataUrl);

  // Compute target size
  let { width, height } = img;
  if (width > MAX_DIM || height > MAX_DIM) {
    if (width > height) {
      height = Math.round(height * (MAX_DIM / width));
      width = MAX_DIM;
    } else {
      width = Math.round(width * (MAX_DIM / height));
      height = MAX_DIM;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Detect WebP support
  const supportsWebP = canvas.toDataURL('image/webp', 0.1).indexOf('data:image/webp') === 0;
  const mime = supportsWebP ? 'image/webp' : 'image/jpeg';

  let quality = TARGET_QUALITY;
  let result = canvas.toDataURL(mime, quality);
  // Decrease quality until size fits target
  while (estimateSize(result) > MAX_SIZE_BYTES && quality > 0.3) {
    quality -= 0.1;
    result = canvas.toDataURL(mime, quality);
  }
  return result;
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function estimateSize(dataUrl) {
  // base64 → bytes ≈ length * 3/4
  const b64 = dataUrl.split(',')[1] || '';
  return Math.floor(b64.length * 0.75);
}
